import { parseJWTPayload } from './permDecrypt';

export const STORAGE_KEY_SELECTED_CONNECTORS = 'stride_ai_selected_connectors';
export const STORAGE_KEY_SELECTED_PROJECT = 'stride_ai_selected_project';
export const DEFAULT_CONNECTORS = ['adobe_target'];

/**
 * Safely extracts the `sub` claim from a JWT token string.
 * Returns null if the token is missing, invalid, or not yet resolved.
 */
export const getUserIdFromToken = (token) => {
    try {
        if (!token) return null;
        const { sub } = parseJWTPayload(token);
        return sub || null;
    } catch {
        return null;
    }
};

/**
 * Reads the persisted connector selection for the given user.
 * Stored as a plain string array: ["adobe_target", "tealium"]
 * Returns the array or null when nothing is stored / invalid.
 */
export const getStoredConnectors = (userId) => {
    try {
        const key = `${STORAGE_KEY_SELECTED_CONNECTORS}__${userId}`;
        const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed.every((s) => typeof s === 'string')) {
            return parsed;
        }
    } catch {
        // Ignore parsing or storage access errors
    }
    return null;
};

/**
 * Persists the connector selection for the given user.
 * connectorId is always derivable from the array, so it is not stored separately.
 */
export const saveConnectors = (userId, connectors) => {
    try {
        if (!userId || !Array.isArray(connectors) || connectors.length === 0) return;
        const key = `${STORAGE_KEY_SELECTED_CONNECTORS}__${userId}`;
        localStorage.setItem(key, JSON.stringify(connectors));
    } catch {
        // Ignore storage write errors (e.g., quota or private browsing)
    }
};

/**
 * Reads the persisted project selection for the given user.
 * Stored as: { id: string, name: string }
 * Returns the object or null when nothing is stored / invalid.
 */
export const getStoredProject = (userId) => {
    try {
        const key = `${STORAGE_KEY_SELECTED_PROJECT}__${userId}`;
        const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed.id === 'string' && parsed.id.trim()) {
            return { id: parsed.id.trim(), name: parsed.name || '' };
        }
    } catch {
        // Ignore parsing or storage access errors
    }
    return null;
};

/**
 * Persists the project selection for the given user.
 * Pass null/undefined id to clear the stored project.
 */
export const saveProject = (userId, project) => {
    try {
        if (!userId) return;
        const key = `${STORAGE_KEY_SELECTED_PROJECT}__${userId}`;
        if (project?.id) {
            localStorage.setItem(key, JSON.stringify({ id: project.id, name: project.name || '' }));
        } else {
            localStorage.removeItem(key);
        }
    } catch {
        // Ignore storage write errors (e.g., quota or private browsing)
    }
};

